import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  // Collapse, // Commented out - not used after removing expandable section
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore"; // Commented out - not used
// import ExpandLessIcon from "@mui/icons-material/ExpandLess"; // Commented out - not used
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { recordAgentAction } from "../../../store/agentSlice";
import styles from "./redline-agent.module.scss";

interface ReferenceText {
  old_text: string | null;
  new_text: string | null;
}

interface ChangeItem {
  clause: string;
  change_detected: string;
  category: string;
  risk: string;
  counter_party_comment: string;
  memory_flag?: string;
  recommended_next_steps: string;
  reference: ReferenceText;
  according_to_parent_msa?: string;
}

interface SummaryOutput {
  needs_attention: ChangeItem[];
  minor: ChangeItem[];
  overall_risk: string;
  contract_type?: string;
  thread_id?: string;
}

interface RedlineData {
  redlined_blob_url: string;
  container: string;
  insertions: number;
  deletions: number;
  edits: number;
  summary_output?: SummaryOutput;
}

interface ParsedContent {
  finalAnswer?: RedlineData;
}

interface RedlineAgentProps {
  parsedContent: ParsedContent;
}

// Expandable Row Component
function ExpandableRow({
  item,
  showCounterpartyComment,
  showParentMsaContext,
  showRecommendedNextSteps,
}: {
  item: ChangeItem;
  showCounterpartyComment: boolean;
  showParentMsaContext: boolean;
  showRecommendedNextSteps: boolean;
}) {
  // const [expanded, setExpanded] = useState(false);

  const getRiskColor = (risk: string) => {
    switch (risk.toUpperCase()) {
      case "HIGH":
        return "#ff6b6b";
      case "MEDIUM":
        return "#ffa500";
      case "LOW":
        return "#00d2aa";
      default:
        return "#666";
    }
  };

  return (
    <>
      <TableRow className={styles.tableRow}>
        <TableCell className={styles.tableCell}>
          <Box className={styles.clauseCell}>
            <Typography variant="body2" className={styles.clauseText}>
              {item.clause}
            </Typography>
          </Box>
        </TableCell>
        <TableCell className={styles.tableCell}>
          <Typography variant="body2" className={styles.changeText}>
            {item.change_detected}
            {item.memory_flag && (
              <>
                <br />
                <span className={styles.memory}>
                  {item.memory_flag === "re_inserted"
                    ? "Previously Deleted Clause Re Inserted"
                    : "Previously Deleted Clause Re Deleted"}
                </span>
              </>
            )}
          </Typography>
        </TableCell>
        {showCounterpartyComment && (
          <TableCell className={styles.tableCell}>
            <Box className={styles.longContentCell}>
              <Typography variant="body2" className={styles.counterPartyText}>
                {item.counter_party_comment}
              </Typography>
            </Box>
          </TableCell>
        )}
        {showRecommendedNextSteps && (
          <TableCell className={styles.tableCell}>
            <Box className={styles.longContentCell}>
              <Typography variant="body2" className={styles.nextStepsText}>
                {item.recommended_next_steps}
              </Typography>
            </Box>
          </TableCell>
        )}
        {showParentMsaContext && (
          <TableCell className={styles.tableCell}>
            <Box className={styles.longContentCell}>
              <Typography variant="body2" className={styles.parentMsaText}>
                {item.according_to_parent_msa || ""}
              </Typography>
            </Box>
          </TableCell>
        )}
        <TableCell className={styles.tableCell} align="center">
          <Chip
            label={item.risk}
            size="small"
            className={styles.riskChip}
            style={{
              backgroundColor: getRiskColor(item.risk),
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "11px",
            }}
          />
        </TableCell>
        {/* Details Column - Commented Out */}
        {/* <TableCell className={styles.tableCell} align="center">
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            className={styles.expandButton}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell> */}
      </TableRow>
      {/* Expandable Section - Commented Out */}
      {/* <TableRow>
        <TableCell
          colSpan={5}
          className={styles.expandableCell}
          style={{ padding: 0 }}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box className={styles.expandedContent}>
              {(item.reference.old_text || item.reference.new_text) && (
                <Box className={styles.referenceSection}>
                  <Typography
                    variant="subtitle2"
                    className={styles.detailTitle}
                  >
                    Reference Text
                  </Typography>
                  <Box className={styles.referenceGrid}>
                    {item.reference.old_text && (
                      <Box className={styles.referenceBox}>
                        <Typography
                          variant="caption"
                          className={styles.referenceLabel}
                        >
                          Old Text
                        </Typography>
                        <Typography
                          variant="body2"
                          className={styles.referenceText}
                        >
                          {item.reference.old_text}
                        </Typography>
                      </Box>
                    )}
                    {item.reference.new_text && (
                      <Box className={styles.referenceBox}>
                        <Typography
                          variant="caption"
                          className={styles.referenceLabel}
                        >
                          New Text
                        </Typography>
                        <Typography
                          variant="body2"
                          className={styles.referenceText}
                        >
                          {item.reference.new_text}
                        </Typography>
                      </Box>
                    )}
                    {!item.reference.new_text && (
                      <Box className={styles.referenceBox}>
                        <Typography
                          variant="caption"
                          className={styles.referenceLabel}
                        >
                          New Text
                        </Typography>
                        <Typography
                          variant="body2"
                          className={`${styles.referenceText} ${styles.deletedText}`}
                        >
                          [DELETED]
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow> */}
    </>
  );
}

// Helper function to check if any items have counterparty comments
const hasCounterpartyComments = (items: ChangeItem[]): boolean => {
  return items.some(
    (item) => item.counter_party_comment && item.counter_party_comment.trim(),
  );
};

// Helper function to check if any items have parent MSA context
const hasParentMsaContext = (items: ChangeItem[]): boolean => {
  return items.some(
    (item) =>
      item.according_to_parent_msa && item.according_to_parent_msa.trim(),
  );
};

// Helper function to check if any items have recommended next steps
const hasRecommendedNextSteps = (items: ChangeItem[]): boolean => {
  return items.some(
    (item) => item.recommended_next_steps && item.recommended_next_steps.trim(),
  );
};

export default function RedlineAgent({
  parsedContent,
}: RedlineAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;
  const [downloading, setDownloading] = useState(false);
  const [copiedThreadId, setCopiedThreadId] = useState(false);
  const redlineData = parsedContent?.finalAnswer;

  if (!redlineData || !redlineData.redlined_blob_url) {
    return (
      <Box>
        <Typography variant="body1" color="text.secondary">
          No redlined document available.
        </Typography>
      </Box>
    );
  }

  const summaryOutput = redlineData.summary_output;
  const hasNeedsAttention =
    summaryOutput?.needs_attention && summaryOutput.needs_attention.length > 0;
  const hasMinor = summaryOutput?.minor && summaryOutput.minor.length > 0;

  // Check if counterparty comments exist in either section
  const showCounterpartyInNeedsAttention = Boolean(
    hasNeedsAttention && hasCounterpartyComments(summaryOutput.needs_attention),
  );
  const showCounterpartyInMinor = Boolean(
    hasMinor && hasCounterpartyComments(summaryOutput.minor),
  );

  // Check if parent MSA context exists in either section
  const showParentMsaInNeedsAttention = Boolean(
    hasNeedsAttention && hasParentMsaContext(summaryOutput.needs_attention),
  );
  const showParentMsaInMinor = Boolean(
    hasMinor && hasParentMsaContext(summaryOutput.minor),
  );

  // Check if recommended next steps exist in either section
  const showRecommendedNextStepsInNeedsAttention = Boolean(
    hasNeedsAttention && hasRecommendedNextSteps(summaryOutput.needs_attention),
  );
  const showRecommendedNextStepsInMinor = Boolean(
    hasMinor && hasRecommendedNextSteps(summaryOutput.minor),
  );

  const handleDownload = () => {
    if (!redlineData.redlined_blob_url) {
      alert("Download URL is not available. Please try again.");
      return;
    }

    setDownloading(true);
    try {
      // Check if redlined_blob_url is already a complete URL
      const isCompleteUrl =
        redlineData.redlined_blob_url.startsWith("http://") ||
        redlineData.redlined_blob_url.startsWith("https://");

      const downloadUrl = isCompleteUrl
        ? redlineData.redlined_blob_url
        : `${import.meta.env.VITE_API_BASE_URL}/download/${redlineData.container}/${redlineData.redlined_blob_url}`;

      const filename =
        redlineData.redlined_blob_url.split("/").pop()?.split("?")[0] ||
        "redlined_document.docx";

      // Record download action - fire and forget (non-blocking)
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "download",
          content: JSON.stringify({
            fileName: filename,
            downloadUrl: downloadUrl,
            redlineStats: {
              insertions: redlineData.insertions || 0,
              deletions: redlineData.deletions || 0,
              edits: redlineData.edits || 0,
              totalChanges:
                (redlineData.insertions || 0) +
                (redlineData.deletions || 0) +
                (redlineData.edits || 0),
            },
            overallRisk: summaryOutput?.overall_risk || "N/A",
          }),
        }),
      );

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading redlined document:", error);
      alert("Failed to download the redlined document. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const totalChanges =
    redlineData.insertions + redlineData.deletions + redlineData.edits;

  const getOverallRiskColor = (risk: string) => {
    switch (risk?.toUpperCase()) {
      case "HIGH":
        return "#ff6b6b";
      case "MEDIUM":
        return "#ffa500";
      case "LOW":
        return "#00d2aa";
      default:
        return "#666";
    }
  };

  return (
    <Box className={styles.container}>
      {/* Header Section */}
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <CheckCircleIcon className={styles.successIcon} />
          <Typography variant="h6" className={styles.title}>
            Redlined Document Ready
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            downloading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={handleDownload}
          disabled={downloading}
          className={styles.downloadButton}
        >
          {downloading ? "Downloading..." : "Download"}
        </Button>
      </Box>

      {/* Stats Section */}
      <Box className={styles.statsContainer}>
        <Typography variant="subtitle1" className={styles.statsTitle}>
          {summaryOutput?.contract_type
            ? `${summaryOutput.contract_type} document changes summary`
            : "Document changes summary"}
        </Typography>

        {/* Thread ID Display */}
        {summaryOutput?.thread_id && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography
              variant="body2"
              component="div"
              sx={{
                fontSize: "13px",
                color: "#004141",
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <span>
                Thread ID:{" "}
                <Typography
                  component="span"
                  sx={{
                    fontSize: "13px",
                    color: "#666",
                    fontWeight: 400,
                    fontFamily: "monospace",
                  }}
                >
                  {summaryOutput.thread_id}
                </Typography>
              </span>
              <Tooltip
                title={copiedThreadId ? "Copied!" : "Copy Thread ID"}
                arrow
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      summaryOutput.thread_id || "",
                    );
                    setCopiedThreadId(true);
                    setTimeout(() => setCopiedThreadId(false), 2000);
                  }}
                  sx={{
                    padding: 0,
                    color: copiedThreadId ? "#00a888" : "#666",
                    "&:hover": {
                      backgroundColor: "#f0faf8",
                      color: "#00a888",
                    },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </Tooltip>
            </Typography>
          </Box>
        )}

        <Box className={styles.statsGrid}>
          {/* Total Changes */}
          <Box className={styles.statItem}>
            <Typography
              variant="h4"
              className={`${styles.statNumber} ${styles.total}`}
            >
              {totalChanges}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Total Changes
            </Typography>
          </Box>

          {/* Insertions */}
          <Box className={styles.statItem}>
            <Typography
              variant="h4"
              className={`${styles.statNumber} ${styles.insertions}`}
            >
              {redlineData.insertions}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Insertions
            </Typography>
          </Box>

          {/* Deletions */}
          <Box className={styles.statItem}>
            <Typography
              variant="h4"
              className={`${styles.statNumber} ${styles.deletions}`}
            >
              {redlineData.deletions}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Deletions
            </Typography>
          </Box>

          {/* Edits */}
          <Box className={styles.statItem}>
            <Typography
              variant="h4"
              className={`${styles.statNumber} ${styles.edits}`}
            >
              {redlineData.edits}
            </Typography>
            <Typography variant="body2" className={styles.statLabel}>
              Modifications
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Needs Attention Table */}
      {hasNeedsAttention && (
        <Box className={styles.tableSection}>
          <Box className={styles.tableTitleSection}>
            <WarningIcon className={styles.tableTitleIcon} />
            <Typography variant="h6" className={styles.tableTitle}>
              Needs Attention
            </Typography>
            <Chip
              label={summaryOutput.needs_attention.length}
              size="small"
              className={styles.countChip}
            />
          </Box>

          <TableContainer className={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow className={styles.tableHeaderRow}>
                  <TableCell className={styles.tableHeader} width="15%">
                    Clause Type
                  </TableCell>
                  <TableCell className={styles.tableHeader} width="20%">
                    Change Detected
                  </TableCell>
                  {showCounterpartyInNeedsAttention && (
                    <TableCell className={styles.tableHeader} width="20%">
                      Counterparty Comment
                    </TableCell>
                  )}
                  {showRecommendedNextStepsInNeedsAttention && (
                    <TableCell className={styles.tableHeader} width="20%">
                      Recommended Next Steps
                    </TableCell>
                  )}
                  {showParentMsaInNeedsAttention && (
                    <TableCell className={styles.tableHeader} width="20%">
                      Parent MSA Context
                    </TableCell>
                  )}
                  <TableCell
                    className={styles.tableHeader}
                    width="10%"
                    align="center"
                  >
                    Risk Level
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryOutput.needs_attention.map((item, index) => (
                  <ExpandableRow
                    key={index}
                    item={item}
                    showCounterpartyComment={showCounterpartyInNeedsAttention}
                    showParentMsaContext={showParentMsaInNeedsAttention}
                    showRecommendedNextSteps={
                      showRecommendedNextStepsInNeedsAttention
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Minor Changes Table */}
      {hasMinor && (
        <Box className={styles.tableSection}>
          <Box className={styles.tableTitleSection}>
            <InfoIcon
              className={styles.tableTitleIcon}
              style={{ color: "#00d2aa" }}
            />
            <Typography variant="h6" className={styles.tableTitle}>
              Minor
            </Typography>
            <Chip
              label={summaryOutput.minor.length}
              size="small"
              className={styles.countChip}
            />
          </Box>

          <TableContainer className={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow className={styles.tableHeaderRow}>
                  <TableCell className={styles.tableHeader} width="15%">
                    Clause Type
                  </TableCell>
                  <TableCell className={styles.tableHeader} width="20%">
                    Change Detected
                  </TableCell>
                  {showCounterpartyInMinor && (
                    <TableCell className={styles.tableHeader} width="20%">
                      Counterparty Comment
                    </TableCell>
                  )}
                  {showRecommendedNextStepsInMinor && (
                    <TableCell className={styles.tableHeader} width="20%">
                      Recommended Next Steps
                    </TableCell>
                  )}
                  {showParentMsaInMinor && (
                    <TableCell className={styles.tableHeader} width="20%">
                      Parent MSA Context
                    </TableCell>
                  )}
                  <TableCell
                    className={styles.tableHeader}
                    width="10%"
                    align="center"
                  >
                    Risk Level
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryOutput.minor.map((item, index) => (
                  <ExpandableRow
                    key={index}
                    item={item}
                    showCounterpartyComment={showCounterpartyInMinor}
                    showParentMsaContext={showParentMsaInMinor}
                    showRecommendedNextSteps={showRecommendedNextStepsInMinor}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
